<script setup lang="ts">
import { ArrowLeft, ArrowRight, DownloadIcon, Loader } from 'lucide-vue-next'
import { storeToRefs } from 'pinia'
import { VisuallyHidden } from 'reka-ui'
import { computed, ref, watch } from 'vue'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useDocumentStore } from '@/stores/document'

const currentPage = ref(1)
const isLoading = ref(true)
const { open, url, name } = storeToRefs(useDocumentStore())

useHead({
  title: `${name.value}`,
  meta: [
    { name: 'viewport', content: 'width=device-width, initial-scale=1, maximum-scale=1' },
  ],
})

// Вычисляем URL для iframe с указанием страницы (если currentPage > 1)
const pdfUrlWithPage = computed(() => {
  if (!url.value)
    return ''
  return currentPage.value > 1
    ? `${url.value}#page=${currentPage.value}`
    : url.value
})

// При открытии диалога сбрасываем номер страницы и включаем лоадер
watch(() => open.value, (val) => {
  if (val) {
    isLoading.value = true
    currentPage.value = 1
  }
})

// Обработчик загрузки iframe
function handleIframeLoad() {
  isLoading.value = false
}

function downloadDocument() {
  if (!url.value)
    return
  const link = document.createElement('a')
  link.href = url.value
  link.target = '_blank'
  link.download = name.value || 'document.pdf'
  link.click()
}
</script>

<template>
  <Dialog v-model:open="open" class="max-w-4xl w-full">
    <DialogContent class="max-w-4xl w-full">
      <DialogHeader class="flex flex-row items-center justify-between gap-4">
        <VisuallyHidden>
          <DialogTitle>{{ name }}</DialogTitle>
        </VisuallyHidden>
        <div class="flex items-center gap-2">
          <Button variant="ghost" size="sm" aria-label="Скачать документ" @click="downloadDocument">
            <span class="hidden sm:inline">Скачать</span>
            <DownloadIcon class="w-4 h-4 sm:ml-2" />
          </Button>
        </div>
      </DialogHeader>

      <div class="relative min-h-[400px]">
        <div v-if="isLoading" class="absolute inset-0 flex items-center justify-center">
          <div class="flex flex-col items-center gap-2 text-muted-foreground">
            <Loader class="w-8 h-8 animate-spin" />
            <span>Загрузка документа...</span>
          </div>
        </div>

        <div v-show="!isLoading" class="overflow-auto max-h-[85vh]">
          <iframe
            :src="pdfUrlWithPage"
            class="pdf-viewer"
            width="100%"
            height="600"
            frameborder="0"
            @load="handleIframeLoad"
          />
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<style>
.pdf-viewer {
  margin: 0 auto;
  min-width: min(100%, 800px);
  height: 600px;
}

@media (max-width: 640px) {
  .pdf-viewer {
    min-width: 100%;
    height: calc(100vh - 200px); /* Высота окна минус отступы для заголовка и кнопок */
  }
}
</style>
